<template>
  <div>
    <section class="p-box">
      <Loading v-if="gmbStoresLoading" />
      <div v-else-if="noLinked" class="p-box__body">
        <h2 class="c-pageTitle">
          {{ $t('components.RequireGmbConnect.title') }}
        </h2>
        <p>{{ $t('pages.CompaniesProducts.gmb.notice') }}</p>
        <div class="c-button c-button--left">
          <router-link
            :to="{ name: 'CompaniesGoogleOauth', params: { company_uuid: companyUuid }}"
            class="c-button__item c-button__item--primary"
          >
            {{ $t('pages.CompaniesProducts.gmb.doConnect') }}
          </router-link>
        </div>
      </div>
      <div v-else>
        <Loading v-show="loading" />
        <template v-if="!loading && categories">
          <KkTutorial
            v-if="categories.length === 0"
            :description1="$t(`pages.CompaniesProductNew.emptyCategory.description1`)"
            :description2="$t(`pages.CompaniesProductNew.emptyCategory.description2`)"
            :img-path="categoryImagePath"
            :tutorial-description="$t(`pages.CompaniesProductNew.emptyCategory.contents`).replace(/\n/g,'<br/>')"
            class="noCategories"
          >
            <template #buttonArea>
              <div class="c-button noCategories__button">
                <router-link :to="{ name: 'CompaniesManagementProductCategories', params: { company_uuid: companyUuid }}"
                             class="c-button__item c-button__item--primary"
                >
                  {{ $t("pages.CompaniesProductNew.linkToCategory") }}
                </router-link>
              </div>
            </template>
          </KkTutorial>
          <template v-else>
            <div class="p-box__header">
              <div class="linkToIndex">
                <router-link class="c-button__item c-button__item--outline" :to="{name: 'CompaniesProducts', params: {companyUuid: companyUuid}}">
                  {{ $t("pages.CompaniesProductNew.linkToIndex") }}
                </router-link>
              </div>
            </div>
            <div class="p-box__body">
              <ValidationObserver v-slot="{ valid }">
                <div class="c-grid__items">
                  <section class="c-grid__item c-grid__item__s__12 c-grid__item__m__8 c-grid__item__l__8">
                    <div class="p-box">
                      <div class="p-box__body">
                        <h3 class="product__title">{{ $t("pages.CompaniesProductNew.form.title") }}</h3>
                        <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <MediaImage
                            :images="images"
                            :loading="loading"
                            :max-files="1"
                            @drop-images="onDropMediaImage"
                            @remove-image="onRemoveMediaImage"
                          />
                        </fieldset>
                        <Info>
                          <p>{{ $t("pages.CompaniesProductNew.form.info.image") }}</p>
                        </Info>
                        <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <ValidationProvider v-slot="{ value, handleChange, errors }" v-model="productNameLabel" :name="$t('pages.CompaniesProductNew.form.label.productName')" rules="required">
                            <label class="c-formLabel">
                              {{ $t("pages.CompaniesProductNew.form.label.productName") }}
                              <span>({{ $t('labels.countCharacters', { count: productNameLabel.length, max: 58 }) }}){{ $t('labels.requiredLabel') }}</span>
                              <span class="c-error-msg">{{ errors[0] }}</span>
                            </label>
                            <TextField
                              :value="value"
                              maxlength="58"
                              :placeholder="$t('pages.CompaniesProductNew.form.placeholder.productName')"
                              @input="handleChange"
                            />
                          </ValidationProvider>
                        </fieldset>
                        <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <ValidationProvider v-slot="{ value, handleChange, errors }" v-model="productCategoryId" :name="$t('pages.CompaniesProductNew.form.label.productCategory')" rules="required">
                            <label class="c-formLabel">
                              {{ $t("pages.CompaniesProductNew.form.label.productCategory") }}
                              <span>{{ $t('labels.requiredLabel') }}</span>
                              <span class="c-error-msg">{{ errors[0] }}</span>
                            </label>
                            <div class="c-select">
                              <select :value="value" @change="handleChange">
                                <option v-for="category in categories" :key="category.id" :value="category.id">
                                  {{ category.name }}
                                </option>
                              </select>
                            </div>
                          </ValidationProvider>
                        </fieldset>
                        <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <ValidationProvider v-slot="{ value, handleChange, errors }" v-model="productPrice" :name="$t('pages.CompaniesProductNew.form.label.productPrice')" rules="numeric">
                            <label class="c-formLabel">
                              {{ $t("pages.CompaniesProductNew.form.label.productPrice") }}
                              <span>{{ $t('labels.optionalLabel') }}</span>
                              <span class="c-error-msg">{{ errors[0] }}</span>
                            </label>
                            <TextField
                              :value="value"
                              :placeholder="$t('pages.CompaniesProductNew.form.label.productPrice')"
                              @input="handleChange"
                              @wheel="$event.target.blur()"
                            />
                          </ValidationProvider>
                        </fieldset>
                        <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <ValidationProvider v-slot="{ value, handleChange, errors }" v-model="productDescription" :name="$t('pages.CompaniesProductNew.form.label.productDescription')" rules="">
                            <label class="c-formLabel">
                              {{ $t("pages.CompaniesProductNew.form.label.productDescription") }}
                              <span>({{ $t('labels.countCharacters', { count: productDescription.length, max: 1000 }) }}){{ $t('labels.optionalLabel') }}</span>
                              <span class="c-error-msg">{{ errors[0] }}</span>
                            </label>
                            <textarea
                              :value="value"
                              class="c-textArea"
                              maxlength="1000"
                              :placeholder="$t('pages.CompaniesProductNew.form.label.productDescription')"
                              @input="handleChange"
                            />
                          </ValidationProvider>
                        </fieldset>
                        <Info>
                          <p>{{ $t('pages.CompaniesProductNew.form.info.productDescription') }}</p>
                        </Info>
                        <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <ValidationProvider v-slot="{ value, handleChange, errors }" v-model="optionButtonUrl" :name="$t('pages.CompaniesProductNew.form.label.productOptionButtonUrl')" rules="url">
                            <div class="formLabelWrapper">
                              <label class="c-formLabel">
                                {{ $t('pages.CompaniesProductNew.form.label.productOptionButtonUrl') }}
                                <span>({{ $t('labels.countCharacters', { count: optionButtonUrl.length, max: 1000 }) }}){{ $t('labels.optionalLabel') }}</span>
                                <span class="c-error-msg">{{ errors[0] }}</span>
                              </label>
                              <KkInsertParameter
                                class="insertParameter"
                                :parameters="parameters"
                                :loading="parameterLoading"
                                @insert-parameter="insertParam"
                              />
                            </div>
                            <TextField
                              ref="optionButtonUrl"
                              :value="value"
                              maxlength="1000"
                              :placeholder="$t('pages.CompaniesProductNew.form.label.productOptionButtonUrl')"
                              @input="handleChange"
                            />
                            <p>{{ $t('pages.CompaniesProductNew.form.info.productOptionButtonUrl') }}</p>
                            <div class="infoContainer">
                              <KkInfo>
                                {{ $t('pages.CompaniesProductNew.form.info.storeParameter') }}
                              </KkInfo>
                            </div>
                          </ValidationProvider>
                        </fieldset>
                      </div>
                    </div>
                  </section>
                  <section class="c-grid__item c-grid__item__s__12 c-grid__item__m__4 c-grid__item__l__4">
                    <div class="p-box">
                      <SelectStore :selected-store-count="storeUuids.length" @open-store-modal="handleOpenStoreModal">
                        <div class="c-info">
                          <div class="c-info__icon"><Icon name="iconQuestion" /></div>
                          <p class="c-info__content">
                            {{ $t('pages.CompaniesProductNew.form.info.selectStore') }}
                          </p>
                        </div>
                        <div class="c-button">
                          <button
                            type="button"
                            class="c-button__item c-button__item--outline-cancel"
                            @click="cancelPage()"
                          >
                            {{ $t('labels.cancel') }}
                          </button>
                          <button
                            type="button"
                            class="c-button__item c-button__item--primary"
                            :class="isFormDisabled || !valid ? 'c-button__item--disabled' : ''"
                            :disabled="isFormDisabled || !valid"
                            @click="openPreviewModal()"
                          >
                            {{ $t('labels.save') }}
                          </button>
                        </div>
                      </SelectStore>
                    </div>
                  </section>
                </div>
              </ValidationObserver>
            </div>
          </template>
        </template>
      </div>
      <StoreCountFilterModal
        v-if="isShowFilterModal"
        @close="handleCloseFilterModal"
        @submit="handleFilterSubmit"
        @reset="handleFilterReset"
      />
      <StoreModal
        v-if="isShowStoreModal"
        store-name="companiesProduct"
        @close-store-modal="confirmStoreUuids"
        @back-filter-modal="handleStoreModalBack"
      />
      <KkProductPreviewModal
        v-if="showPreviewModal"
        :is-visible="showPreviewModal"
        :images="images"
        :product-name="productNameLabel"
        :category-name="categoryName"
        :product-price="productPrice"
        :product-description="productDescription"
        :option-button-url="optionButtonUrl"
        @submit="submitProduct"
        @close="closePreviewModal"
      />
    </section>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import Loading from '../../../components/Loading.vue'
import Icon from '../../../components/Icon'
import categoryImagePath from '../../../../../images/products/category.png'
import SelectStore from '../../../components/CompaniesProducts/SelectStore'
import StoreCountFilterModal from '../../../components/StoreCountFilterModal'
import StoreModal from '../../../components/StoreModal'
import TextField from '../../../components/TextField'
import Info from '../../../components/Info'
import MediaImage from '../../../components/MediaImage'
import { KkInsertParameter, KkTutorial } from '../../../components/projects'
import { KkInfo } from '../../../components/ui'
import { KkProductPreviewModal } from '../../../components/domains/product'

export default {
  name: 'CompaniesProductForm',
  components: {
    Loading,
    SelectStore,
    Icon,
    StoreCountFilterModal,
    StoreModal,
    TextField,
    Info,
    MediaImage,
    KkInsertParameter,
    KkInfo,
    KkProductPreviewModal,
    KkTutorial,
  },
  data () {
    return {
      isShowFilterModal: false,
      isShowStoreModal: false,
      isEditImage: false,
      isProcessingSubmit: false,
      showPreviewModal: false,
      productNameLabel: '',
      productCategoryId: null,
      productPrice: null,
      productDescription: '',
      optionButtonUrl: '',
      images: [],
      storeUuids: [],
    }
  },
  computed: {
    ...mapState({
      errors: state => state.companiesProductCategories.errors,
      product: state => state.companiesProduct.data,
      formValues: state => state.companiesProduct.formValues,
      productLoading: state => state.companiesProduct.loading,
      categories: state => state.companiesProductCategories.data,
      categories_loading: state => state.companiesProductCategories.loading,
      searchStoreLoading:  state => state.companiesGmbSearchStores.loading,
      brands: state => state.companiesSearchTagGroups.data.brands,
      franchisees: state => state.companiesSearchTagGroups.data.franchisees,
      areas: state => state.companiesSearchTagGroups.data.areas,
      userCompanies: state => state.companiesSearchTagGroups.data.user_companies,
      prefectures : state => state.companiesSearchTagGroups.data.prefectures,
      searchAttributesLoading : state => state.companiesSearchTagGroups.loading,
      gmbStores: state => state.companiesGmbStores.data,
      gmbStoresLoading: state => state.companiesGmbStores.loading,
      originalTagGroupA : state => state.companiesSearchTagGroups.data.original_tag_group_a,
      originalTagGroupB : state => state.companiesSearchTagGroups.data.original_tag_group_b,
      originalTagGroupC : state => state.companiesSearchTagGroups.data.original_tag_group_c,
      originalTagGroupD : state => state.companiesSearchTagGroups.data.original_tag_group_d,
      originalTagGroupE : state => state.companiesSearchTagGroups.data.original_tag_group_e,
    }),
    ...mapGetters({
      noLinked: 'companiesGmbStores/noLinked',
      availableParameters: 'companiesParameters/availableParameters',
      parameterLoading: 'companiesParameters/loading',
    }),
    loading() {
      return this.categories_loading || this.searchStoreLoading || this.searchAttributesLoading || this.productLoading
    },
    categoryImagePath: () => categoryImagePath,
    isFormDisabled() {
      return this.images.length !== 1 || this.isProcessingSubmit
    },
    companyUuid() {
      return this.$route.params.company_uuid
    },
    currentId() {
      return this.$route.params.id
    },
    parameters() { return this.availableParameters.filter(v => v != 'REVIEWER_NAME') },
    categoryName() {
      return this.categories.find(v => v.id == this.productCategoryId).name
    }
  },
  async created() {
    await this.readGmbStores(this.companyUuid)
    if(this.currentId){
      await this.readProduct({ company_uuid: this.companyUuid, product_id: this.currentId })
      this.productNameLabel = this.product.title || ''
      this.productCategoryId = this.product.productCategoryId
      this.productPrice = this.product.price?.value?.toString()
      this.productDescription = this.product.description || ''
      this.optionButtonUrl = this.product.optionButton?.url || ''
      this.images = this.product.image ? [{ url: this.product.image, id: 1, name: '' }] : []
      this.storeUuids = this.product.productStores ? this.product.productStores.map(store => store.uuid) : []
    }else{
      this.initNewProduct()
      this.resetMediaImages()
    }
    this.readCategories()
    this.readParameters()
    await this.readSearchTagGroups()
    this.readStores({companyUuid: this.companyUuid, formValues: {}})
    this.setGmbSearchAttributesFormStoreValues(this.formInitialValues())
  },
  methods: {
    ...mapActions({
      readCategories: 'companiesProductCategories/readProductCategories',
      readStores: 'companiesGmbSearchStores/readStores',
      readSearchTagGroups: 'companiesSearchTagGroups/readSearchTagGroups',
      initNewProduct: 'companiesProduct/initNewProduct',
      createProduct: 'companiesProduct/createProduct',
      updateProduct: 'companiesProduct/updateProduct',
      readProduct: 'companiesProduct/readProduct',
      createPreview: 'companiesProduct/createPreview',
      readGmbStores: 'companiesGmbStores/readGmbStores',
      readParameters: 'companiesParameters/readParameters',
    }),
    ...mapMutations({
      setGmbSearchAttributesFormStoreValues: 'companiesSearchTagGroups/setFormValues',
      setFormValues: 'companiesProduct/setFormValues'
    }),
    handleOpenStoreModal() {
      if (this.storeUuids.length !== 0) {
        this.isShowStoreModal = true
      } else {
        this.isShowFilterModal = true
      }
    },
    handleCloseFilterModal() {
      this.setFormValues({ stores: [...this.storeUuids] })
      this.isShowFilterModal = false
    },
    cancelPage() {
      if (confirm(this.$t('labels.cancelInput'))) {
        this.$router.push({ name: 'CompaniesProducts', params: { company_uuid: this.companyUuid } })
      }
    },
    productRequestPayload() {
      const payload = {
        product: {
          product_category_id: this.productCategoryId,
          title: this.productNameLabel,
          description: this.productDescription,
        },
        price: {
          value: this.productPrice
        },
        option_button: {
          url: this.optionButtonUrl
        },
        store_uuids: this.storeUuids
      }
      return payload
    },
    async submitProduct() {
      if (this.isProcessingSubmit) return
      this.isProcessingSubmit = true
      this.closePreviewModal()
      const payload = this.productRequestPayload()

      let isSuccess
      const doRequest = async (payloadData) => {
        if(this.currentId) {
          isSuccess = await this.updateProduct({ company_uuid: this.companyUuid, product_id: this.currentId, payload: payloadData })
          if(isSuccess) { this.$router.push({ name: 'CompaniesProduct', params: { company_uuid: this.companyUuid, id: this.currentId } }) }
        } else {
          isSuccess = await this.createProduct({ company_uuid: this.companyUuid, payload: payloadData })
          if(isSuccess) { this.$router.push({ name: 'CompaniesProducts', params: { company_uuid: this.companyUuid } }) }
        }
        this.isProcessingSubmit = false
      }

      if (!this.isEditImage) { return await doRequest(payload) }

      const reader = new FileReader()
      reader.readAsDataURL(this.images[0].file)
      reader.onload = async () => {
        payload.product.filename = this.images[0].file.name
        payload.product.image = reader.result

        await doRequest(payload)
      }
    },
    handleFilterSubmit(){
      this.isShowFilterModal = false
      this.isShowStoreModal = true
    },
    handleFilterReset() {
      this.isShowFilterModal = false
    },
    handleStoreModalBack() {
      this.isShowStoreModal = false
      this.isShowFilterModal = true
    },
    formInitialValues() {
      return {
        brands: this.brands.tags.map(v => v.id),
        franchisees: this.franchisees.tags.map(v => v.id),
        areas: this.areas.tags.map(v => v.id),
        user_companies: this.userCompanies.tags.map(v => v.id),
        prefectures: this.prefectures.tags.map(v => v.id),
        original_tag_group_a: this.originalTagGroupA?.tags.map(v => v.id),
        original_tag_group_b: this.originalTagGroupB?.tags.map(v => v.id),
        original_tag_group_c: this.originalTagGroupC?.tags.map(v => v.id),
        original_tag_group_d: this.originalTagGroupD?.tags.map(v => v.id),
        original_tag_group_e: this.originalTagGroupE?.tags.map(v => v.id)
      }
    },
    resetMediaImages() {
      this.images = []
    },
    onDropMediaImage(images) {
      const _images = []
      images.forEach((image, index) => {
        _images.push({
          file: image,
          id: index,
          url: window.URL.createObjectURL(image)
        })
      })

      this.images = _images
      this.isEditImage = true
    },
    onRemoveMediaImage(imageId) {
      this.images = this.images.filter((image) => {
        return image.id !== imageId
      })
    },
    confirmStoreUuids(storeUuids) {
      this.isShowStoreModal = false
      this.storeUuids = storeUuids
    },
    insertParam (parameterName) {
      const pos = this.$refs.optionButtonUrl.$refs.textField.selectionStart
      const before = this.optionButtonUrl.substring(0, pos)
      const after = this.optionButtonUrl.substring(pos, this.optionButtonUrl.length)
      this.optionButtonUrl = before + parameterName + after
    },
    async openPreviewModal() {
      const doRequest = async (payloadData) => {
        const isSuccess = await this.createPreview({ company_uuid: this.companyUuid, payload: payloadData })

        if (isSuccess) {
          this.showPreviewModal = true
        }
      }
      const payload = this.productRequestPayload()
      if (!this.isEditImage) { return await doRequest(payload) }

      const reader = new FileReader()
      reader.readAsDataURL(this.images[0].file)
      reader.onload = async () => {
        payload.product.filename = this.images[0].file.name
        payload.product.image = reader.result

        await doRequest(payload)
      }
    },
    closePreviewModal() {
      this.showPreviewModal = false
    },
  }
}
</script>


<style lang="scss" scoped>
.linkToCategory {
  margin-top: 24px;
}
.linkToIndex {
  margin-left: auto;
}
.product {
  &__title {
    margin-bottom: 20px;
    font-weight: 700;
    font-size: 20px;
    line-height: 1.5;
  }
}
.formLabelWrapper {
  display: flex;
  justify-content: space-between;
}
.insertParameter {
  margin-bottom: 8px;
}
.infoContainer {
  margin: 16px 0;
}
.noCategories {
  &__button {
    .c-button__item {
      margin: 0;
    }
  }
}
</style>
